import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import {
	type ContactInfoTngBinaryEntry,
	type ContactInfoTngQueueItem,
	fetchContactInfoTngQueue,
	fetchRecentContactInfoTngItems,
} from "../../api/analytics/contactinfo-tng-queue";
import { formatDateTimeWithSeconds as formatDateTime } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";

interface PersonContactInfoStatusProps {
	personId: number;
}

export const PersonContactInfoStatus = ({ personId }: PersonContactInfoStatusProps) => {
	const { currentUser } = useGlobalState();
	const isSuperuser = !!currentUser?.is_superuser;

	const [queueItems, setQueueItems] = useState<ContactInfoTngQueueItem[]>([]);
	const [binaryResults, setBinaryResults] = useState<ContactInfoTngBinaryEntry[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		if (!isSuperuser || !personId) return;

		setLoading(true);
		Promise.all([
			fetchContactInfoTngQueue(100, 0, personId),
			fetchRecentContactInfoTngItems(100, 0, personId),
		])
			.then(([queueData, binaryData]) => {
				setQueueItems(queueData.results || []);
				setBinaryResults(binaryData.results || []);
			})
			.catch((error) => {
				console.error("Failed to fetch ContactInfo TNG data:", error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [isSuperuser, personId]);

	if (!isSuperuser) return null;

	return (
		<Grid size={12} data-component="person-contactinfo-status">
			<Accordion data-component="person-contactinfo-accordion">
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="person-contactinfo-accordion-summary"
				>
					<Stack direction="row" spacing={2} alignItems="center">
						<Typography
							variant="h6"
							data-component="person-contactinfo-title"
						>
							ContactInfo TNG Status
						</Typography>
						<Tooltip title="Open ContactInfo TNG Queue page">
							<IconButton
								size="small"
								href="/analytics/contactinfo-tng-queue"
								target="_blank"
								onClick={(e) => e.stopPropagation()}
								data-component="contactinfo-tng-queue-link"
							>
								<OpenInNewIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						{queueItems.length > 0 && (
							<Chip
								label={`${queueItems.length} queued`}
								size="small"
								color="info"
								data-component="queue-count-chip"
							/>
						)}
						{binaryResults.length > 0 && (
							<Chip
								label={`${binaryResults.length} results`}
								size="small"
								color="primary"
								data-component="results-count-chip"
							/>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails data-component="person-contactinfo-accordion-details">
					<Card data-component="person-contactinfo-card">
						<CardContent data-component="person-contactinfo-card-content">
							{loading ? (
								<Typography variant="body2" color="text.secondary">
									Loading...
								</Typography>
							) : (
								<Stack spacing={3}>
									{/* Queue Items Section */}
									<Box data-component="queue-items-section">
										<Typography variant="subtitle1" gutterBottom>
											Queue Items (Pending ContactInfo Checks)
										</Typography>
										{queueItems.length > 0 ? (
											<Table
												size="small"
												data-component="queue-items-table"
											>
												<TableHead>
													<TableRow>
														<TableCell>Priority</TableCell>
														<TableCell>Bot</TableCell>
														<TableCell>Type</TableCell>
														<TableCell>Created</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{queueItems.map((item) => (
														<TableRow key={item.id} hover>
															<TableCell>
																{item.priority}
															</TableCell>
															<TableCell>
																{item.bot_name}
															</TableCell>
															<TableCell>
																{item.type}
															</TableCell>
															<TableCell>
																{formatDateTime(
																	item.created
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No pending queue items
											</Typography>
										)}
									</Box>

									{/* Binary Results Section */}
									<Box data-component="binary-results-section">
										<Typography variant="subtitle1" gutterBottom>
											ContactInfo Results
										</Typography>
										{binaryResults.length > 0 ? (
											<Table
												size="small"
												data-component="binary-results-table"
											>
												<TableHead>
													<TableRow>
														<TableCell>Bot</TableCell>
														<TableCell>Extracted</TableCell>
														<TableCell>
															Fetched At
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{binaryResults.map((result) => (
														<TableRow key={result.id} hover>
															<TableCell>
																{result.bot_name}
															</TableCell>
															<TableCell>
																<Chip
																	label={
																		result.extracted
																			? "YES"
																			: "NO"
																	}
																	size="small"
																	color={
																		result.extracted
																			? "success"
																			: "warning"
																	}
																	data-component="extracted-chip"
																/>
															</TableCell>
															<TableCell>
																{formatDateTime(
																	result.created
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No ContactInfo results
											</Typography>
										)}
									</Box>
								</Stack>
							)}
						</CardContent>
					</Card>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};

export default PersonContactInfoStatus;
